import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Statistic categories with their IDs for PLAYERS
const STAT_CATEGORIES = [
  { id: 1, name: 'Goals', key: 'goals', displayField: 'total_goal' },
  { id: 26, name: 'Assists', key: 'assists', displayField: 'total_statistic' },
  { id: 23, name: 'Shoot on Target', key: 'shoot_on_target', displayField: 'total_statistic' },
  { id: 250, name: 'Total Passes', key: 'total_passes', displayField: 'total_statistic' },
  { id: 18, name: 'Saves', key: 'saves', displayField: 'total_statistic' }
];

// Team stat columns to display
const TEAM_STAT_COLUMNS = [
  { id: 178, name: 'Goals Conceded', key: 'goals_conceded' }
];

const StatistikPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [statsData, setStatsData] = useState({});
  const [loadingStats, setLoadingStats] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [loadingPlayerDetail, setLoadingPlayerDetail] = useState(false);
  
  // Team stats state
  const [teamStats, setTeamStats] = useState([]);
  const [loadingTeamStats, setLoadingTeamStats] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetail, setTeamDetail] = useState(null);
  const [loadingTeamDetail, setLoadingTeamDetail] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [loadingTeamPlayers, setLoadingTeamPlayers] = useState(false);
  const [teamModalTab, setTeamModalTab] = useState('players'); // 'players' or 'stats'
  
  // Unlock state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [tokensBalance, setTokensBalance] = useState(0);
  const [unlockCost, setUnlockCost] = useState(25);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    checkUnlockStatus();
    fetchCompetitions();
  }, []);

  const checkUnlockStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/statistik/unlock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsUnlocked(response.data.unlocked);
      setTokensBalance(response.data.tokens_balance);
      setUnlockCost(response.data.unlock_cost);
    } catch (error) {
      console.error('Error checking unlock status:', error);
    }
  };

  const handleUnlock = async () => {
    if (tokensBalance < unlockCost) {
      toast.error(`Token tidak cukup! Anda butuh ${unlockCost} token, saldo: ${tokensBalance} token`);
      return;
    }
    
    setUnlocking(true);
    try {
      const response = await axios.post(`${API}/api/statistik/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setIsUnlocked(true);
      setTokensBalance(response.data.tokens_balance);
      setShowUnlockModal(false);
    } catch (error) {
      console.error('Error unlocking:', error);
      toast.error(error.response?.data?.detail || 'Gagal unlock fitur statistik');
    } finally {
      setUnlocking(false);
    }
  };

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatForCategory = useCallback(async (competitionId, statCategory) => {
    setLoadingStats(prev => ({ ...prev, [statCategory.key]: true }));
    try {
      const response = await axios.get(`${API}/api/lapangbola/player-top-stats`, {
        params: { 
          competition_id: competitionId, 
          player_statistic_id: statCategory.id 
        }
      });
      
      if (response.data && response.data.data) {
        // Take top 10 players
        const topPlayers = response.data.data.slice(0, 10);
        setStatsData(prev => ({ ...prev, [statCategory.key]: topPlayers }));
      } else {
        setStatsData(prev => ({ ...prev, [statCategory.key]: [] }));
      }
    } catch (error) {
      console.error(`Error fetching ${statCategory.name}:`, error);
      setStatsData(prev => ({ ...prev, [statCategory.key]: [] }));
    } finally {
      setLoadingStats(prev => ({ ...prev, [statCategory.key]: false }));
    }
  }, []);

  // Fetch team stats
  const fetchTeamStats = useCallback(async (competitionId) => {
    setLoadingTeamStats(true);
    try {
      // Fetch Goals Conceded stats (id=178) to get team list with stats
      const response = await axios.get(`${API}/api/lapangbola/team-top-stats`, {
        params: { 
          competition_id: competitionId, 
          match_statistic_id: 178  // Goals Conceded
        }
      });
      
      if (response.data && response.data.data) {
        setTeamStats(response.data.data);
      } else {
        setTeamStats([]);
      }
    } catch (error) {
      console.error('Error fetching team stats:', error);
      setTeamStats([]);
    } finally {
      setLoadingTeamStats(false);
    }
  }, []);

  // Fetch all stats when competition changes
  useEffect(() => {
    if (selectedCompetition) {
      // Clear previous data
      setStatsData({});
      setTeamStats([]);
      // Fetch all player stat categories
      STAT_CATEGORIES.forEach(cat => {
        fetchStatForCategory(selectedCompetition, cat);
      });
      // Fetch team stats
      fetchTeamStats(selectedCompetition);
    }
  }, [selectedCompetition, fetchStatForCategory, fetchTeamStats]);

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
  };

  const handlePlayerClick = async (player) => {
    // Check if unlocked before showing detail
    if (!isUnlocked) {
      setShowUnlockModal(true);
      return;
    }
    
    setSelectedPlayer(player);
    setLoadingPlayerDetail(true);
    try {
      const response = await axios.get(`${API}/api/lapangbola/players/${player.player_id}`);
      // API returns data directly, not in response.data.data
      if (response.data && response.data.status === 'success') {
        setPlayerDetail(response.data);
      } else if (response.data && response.data.id) {
        // Direct data response
        setPlayerDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching player detail:', error);
      toast.error('Gagal memuat detail pemain');
    } finally {
      setLoadingPlayerDetail(false);
    }
  };

  const closePlayerModal = () => {
    setSelectedPlayer(null);
    setPlayerDetail(null);
  };

  const handleTeamClick = async (team) => {
    // Team detail is open by default (no unlock required)
    setSelectedTeam(team);
    setTeamModalTab('players'); // Default to players tab
    setLoadingTeamDetail(true);
    setLoadingTeamPlayers(true);
    setTeamPlayers([]);
    setTeamDetail(null);
    
    try {
      // Fetch TWO API calls:
      // 1. With competition_id to get player_list
      // 2. Without competition_id to get full statistics
      const [playersResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/api/lapangbola/club-detail/${team.club_id}?competition_id=${selectedCompetition}`),
        axios.get(`${API}/api/lapangbola/club-detail/${team.club_id}`)
      ]);
      
      // Get players from response with competition_id
      if (playersResponse.data?.data?.[0]?.player_list) {
        setTeamPlayers(playersResponse.data.data[0].player_list);
      }
      
      // Get stats from response without competition_id
      if (statsResponse.data?.data?.[0]) {
        setTeamDetail(statsResponse.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching team detail:', error);
      toast.error('Gagal memuat detail tim');
    } finally {
      setLoadingTeamDetail(false);
      setLoadingTeamPlayers(false);
    }
  };

  const closeTeamModal = () => {
    setSelectedTeam(null);
    setTeamDetail(null);
    setTeamPlayers([]);
    setTeamModalTab('players');
  };

  // Handle clicking player from team modal (uses same lock check as regular player click)
  const handleTeamPlayerClick = async (player) => {
    // Check if unlocked before showing detail
    if (!isUnlocked) {
      setShowUnlockModal(true);
      return;
    }
    
    // Close team modal and open player detail
    closeTeamModal();
    setSelectedPlayer({
      player_id: player.id,
      player_name: player.name,
      player_picture: player.picture_url || null
    });
    setLoadingPlayerDetail(true);
    setPlayerDetail(null);
    
    try {
      const response = await axios.get(`${API}/api/lapangbola/players/${player.id}`);
      if (response.data) {
        setPlayerDetail(response.data);
      } else {
        toast.error('Detail pemain tidak tersedia');
        setSelectedPlayer(null);
      }
    } catch (error) {
      console.error('Error fetching player detail:', error);
      toast.error('Gagal memuat detail pemain');
      setSelectedPlayer(null);
    } finally {
      setLoadingPlayerDetail(false);
    }
  };

  // Render a single stat column
  const renderStatColumn = (statCategory) => {
    const players = statsData[statCategory.key] || [];
    const isLoading = loadingStats[statCategory.key];

    return (
      <div key={statCategory.key} className="flex-1 min-w-[200px] bg-white rounded-lg shadow-md overflow-hidden">
        {/* Column Header */}
        <div className="bg-gradient-ltb-r text-white px-3 py-3">
          <h3 className="text-sm font-bold flex items-center gap-2">
            {statCategory.name} <span className="text-white/70">›</span>
          </h3>
        </div>
        
        {/* Players List */}
        <div className="divide-y divide-gray-100">
          {isLoading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ltb-bright mx-auto"></div>
            </div>
          ) : players.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">
              Tidak ada data
            </div>
          ) : (
            players.map((player, index) => (
              <div
                key={player.player_id || index}
                onClick={() => handlePlayerClick(player)}
                className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                {/* Rank */}
                <span className={`w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full ${
                  index === 0 ? 'bg-yellow-400 text-yellow-900' :
                  index === 1 ? 'bg-gray-300 text-gray-700' :
                  index === 2 ? 'bg-orange-300 text-orange-800' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {index + 1}
                </span>
                
                {/* Player Photo */}
                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  {player.player_picture ? (
                    <img 
                      src={player.player_picture} 
                      alt={player.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-bold">
                      {player.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                
                {/* Player Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">{player.name}</p>
                  <p className="text-[10px] text-gray-500 truncate">{player.club_name || '-'}</p>
                </div>
                
                {/* Stat Value */}
                <span className="text-lg font-bold text-ltb-bright">
                  {player[statCategory.displayField] || 0}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-ltb pb-20">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white py-4 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/home')}
                className="text-white hover:text-white/80 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Statistik Pemain</h1>
                <p className="text-sm text-white/80 mt-1">Top statistik pemain kompetisi</p>
              </div>
            </div>
            {/* Unlock Status Badge */}
            <div className="flex items-center gap-2">
              {isUnlocked ? (
                <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-semibold flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  Unlocked
                </span>
              ) : (
                <button
                  onClick={() => setShowUnlockModal(true)}
                  className="px-3 py-1 bg-yellow-500 text-white rounded-full text-xs font-semibold flex items-center gap-1 hover:bg-yellow-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                  </svg>
                  Unlock ({unlockCost} Token)
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Competition Selector */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Unlock Banner for non-unlocked users */}
        {!isUnlocked && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
              </svg>
              <div>
                <p className="text-white font-medium">Fitur Detail Pemain Terkunci</p>
                <p className="text-white/70 text-sm">Bayar {unlockCost} token untuk melihat statistik lengkap semua pemain</p>
              </div>
            </div>
            <button
              onClick={() => setShowUnlockModal(true)}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors text-sm"
            >
              Unlock
            </button>
          </div>
        )}
        
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
          <label className="block text-sm font-medium text-white mb-2">
            Pilih Kompetisi
          </label>
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(Number(e.target.value))}
            className="w-full px-4 py-2 border border-white/30 rounded-lg bg-white/20 text-white focus:ring-2 focus:ring-white focus:border-transparent"
          >
            {competitions.length === 0 ? (
              <option className="text-gray-900">Tidak ada kompetisi</option>
            ) : (
              competitions.map(comp => (
                <option key={comp.id} value={comp.id} className="text-gray-900">
                  {comp.name} {comp.season && `(${comp.season})`}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Competition Title */}
        {selectedCompetitionName && (
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold text-white">{selectedCompetitionName}</h2>
          </div>
        )}

        {/* Player Stats Section */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            Statistik Pemain
          </h3>
          {/* Stats Grid - 5 Columns */}
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STAT_CATEGORIES.map(cat => renderStatColumn(cat))}
          </div>
        </div>

        {/* Team Stats Section */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
            Statistik Tim
          </h3>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-ltb-r text-white px-4 py-3 text-sm font-semibold">
              <div>Tim</div>
            </div>
            
            {/* Team List */}
            <div className="divide-y divide-gray-100">
              {loadingTeamStats ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ltb-bright mx-auto"></div>
                </div>
              ) : teamStats.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  Tidak ada data tim
                </div>
              ) : (
                teamStats.map((team, index) => (
                  <div
                    key={team.club_id}
                    onClick={() => handleTeamClick(team)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {team.club_picture ? (
                        <img 
                          src={team.club_picture} 
                          alt={team.club_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-bold">
                          {team.club_name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-gray-900">{team.club_name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Player Detail Modal */}
      {selectedPlayer && (
        <div 
          className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
          onClick={closePlayerModal}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Player Name */}
            <div className="bg-gradient-ltb-r text-white p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-xl uppercase tracking-wide">
                    {playerDetail?.name || selectedPlayer.name}
                  </h3>
                  {playerDetail?.nick_name && playerDetail.nick_name !== playerDetail.name && (
                    <p className="text-white/80 text-sm">{playerDetail.nick_name}</p>
                  )}
                </div>
                <button 
                  onClick={closePlayerModal}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              {loadingPlayerDetail ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                  <p className="text-gray-500">Memuat detail pemain...</p>
                </div>
              ) : playerDetail ? (
                <div className="flex flex-col sm:flex-row">
                  {/* Left Panel - Player Info */}
                  <div className="sm:w-2/5 bg-gray-50 p-4">
                    {/* Player Photo */}
                    <div className="w-full aspect-square max-w-[180px] mx-auto rounded-lg overflow-hidden bg-gray-200 border-4 border-white shadow-lg mb-4">
                      {playerDetail.picture_url || selectedPlayer.player_picture ? (
                        <img 
                          src={playerDetail.picture_url || selectedPlayer.player_picture} 
                          alt={playerDetail.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-5xl font-bold bg-gradient-to-br from-blue-100 to-blue-200">
                          {playerDetail.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>

                    {/* Player Info List */}
                    <div className="w-full space-y-2 text-sm">
                      <div>
                        <p className="text-gray-400 text-xs">Nationality</p>
                        <p className="font-bold text-gray-800">{playerDetail.nationality || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Date of Birth</p>
                        <p className="font-bold text-gray-800">{playerDetail.date_of_birth || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Age</p>
                        <p className="font-bold text-gray-800">{playerDetail.age || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Height</p>
                        <p className="font-bold text-gray-800">{playerDetail.height ? `${playerDetail.height} cm` : '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Weight</p>
                        <p className="font-bold text-gray-800">{playerDetail.weight ? `${playerDetail.weight} kg` : '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Primary Position</p>
                        <p className="font-bold text-gray-800">{playerDetail.position || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-xs">Jersey</p>
                        <p className="font-bold text-gray-800">{playerDetail.number || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Stats */}
                  <div className="sm:w-3/5 p-4">
                    {/* Stats Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-ltb-bright text-white px-3 py-1 rounded text-sm font-semibold">Stats</span>
                    </div>

                    {/* Summary Stats Row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Appearances</p>
                        <p className="font-bold text-ltb-bright text-lg">{playerDetail.total_play || 0}</p>
                        <p className="text-gray-400 text-[10px]">/ {playerDetail.total_sub || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Wins</p>
                        <p className="font-bold text-ltb-bright text-lg">
                          {playerDetail.match_histories?.filter(m => {
                            const isHome = m.club === m.home_club;
                            return isHome ? m.home_score > m.away_score : m.away_score > m.home_score;
                          }).length || 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Losses</p>
                        <p className="font-bold text-ltb-bright text-lg">
                          {playerDetail.match_histories?.filter(m => {
                            const isHome = m.club === m.home_club;
                            return isHome ? m.home_score < m.away_score : m.away_score < m.home_score;
                          }).length || 0}
                        </p>
                      </div>
                    </div>

                    {/* Attack Section - Table Style */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Attack</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Shots</td>
                            <td className="py-2 text-right font-semibold text-gray-800">
                              {(playerDetail.shot_on_target || 0) + (playerDetail.shot_off_target || 0) + (playerDetail.shot_blocked || 0)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Shot Off Target</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.shot_off_target || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Shot On Target</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.shot_on_target || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Goals</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.total_goal || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Team Play Section - Table Style */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Team Play</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Passing Success</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.pass_success || playerDetail.pass || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Assist</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.assist || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Defence Section - Table Style */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Defence</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Defence Success</td>
                            <td className="py-2 text-right font-semibold text-gray-800">
                              {(playerDetail.tackle || 0) + (playerDetail.intercept || 0) + (playerDetail.block || 0) + (playerDetail.clearance || 0)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Tackles</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.tackle || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Intercepts</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.intercept || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Blocks</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.block || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Clearances</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.clearance || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Saves</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.save || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Penalty Saves</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.penalty_save || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Cross Claims</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.cross_claim || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Goals Conceded</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.concede || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Discipline Section - Table Style */}
                    <div>
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Discipline</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Fouls</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.foul || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Yellow Cards</td>
                            <td className="py-2 text-right font-semibold text-yellow-600">{playerDetail.yellow_card || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Red Cards</td>
                            <td className="py-2 text-right font-semibold text-red-600">{playerDetail.red_card || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Detail pemain tidak tersedia</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team Detail Modal */}
      {selectedTeam && (
        <div 
          className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
          onClick={closeTeamModal}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Team Name */}
            <div className="bg-gradient-ltb-r text-white p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {selectedTeam.club_picture && (
                    <img 
                      src={selectedTeam.club_picture} 
                      alt={selectedTeam.club_name}
                      className="w-12 h-12 rounded-lg bg-white/20 object-cover"
                    />
                  )}
                  <div>
                    <h3 className="font-bold text-xl uppercase tracking-wide">
                      {selectedTeam.club_name}
                    </h3>
                    <p className="text-white/80 text-sm">Detail Tim</p>
                  </div>
                </div>
                <button 
                  onClick={closeTeamModal}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setTeamModalTab('players')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  teamModalTab === 'players'
                    ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Pemain ({teamPlayers.length})
              </button>
              <button
                onClick={() => setTeamModalTab('stats')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  teamModalTab === 'stats'
                    ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Statistik
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
              {/* Players Tab */}
              {teamModalTab === 'players' && (
                <div className="p-4">
                  {loadingTeamPlayers ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                      <p className="text-gray-500">Memuat daftar pemain...</p>
                    </div>
                  ) : teamPlayers.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Tidak ada data pemain</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {teamPlayers.map((player, index) => (
                        <div
                          key={player.id}
                          onClick={() => handleTeamPlayerClick(player)}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          {/* Rank Number */}
                          <div className="w-8 h-8 flex items-center justify-center bg-ltb-bright text-white rounded-full text-sm font-bold">
                            {index + 1}
                          </div>
                          
                          {/* Player Photo */}
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            {player.picture_url ? (
                              <img 
                                src={player.picture_url} 
                                alt={player.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                                {player.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          
                          {/* Player Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 truncate">{player.name}</p>
                            <p className="text-xs text-gray-500">{player.position || '-'}</p>
                          </div>
                          
                          {/* Jersey Number */}
                          {player.number && (
                            <div className="bg-ltb-bright text-white px-3 py-1 rounded-lg text-sm font-bold">
                              #{player.number}
                            </div>
                          )}
                          
                          {/* Lock indicator if not unlocked */}
                          {!isUnlocked && (
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Stats Tab */}
              {teamModalTab === 'stats' && (
                <div className="p-4">
                  {loadingTeamDetail ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                      <p className="text-gray-500">Memuat detail tim...</p>
                    </div>
                  ) : teamDetail ? (
                    <div>
                      {/* Summary Stats Row */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Games Played</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.play || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Wins</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.win || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Losses</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.lose || 0}</p>
                        </div>
                      </div>

                      {/* Attack Section - Table Style */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Attack</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shots</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shot Off Target</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot_off_target || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shot On Target</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot_on_target || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Goals</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.goal || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Chances Created</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.create_chance || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Team Play Section - Table Style */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Team Play</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Total Pass</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Pass Success</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass_success || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Pass Accuracy</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass_accuracy || 0}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Defence Section - Table Style */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Defence</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Defence Success</td>
                              <td className="py-2 text-right font-semibold text-gray-800">
                                {(teamDetail.tackle || 0) + (teamDetail.intercept || 0) + (teamDetail.block || 0)}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Tackles</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.tackle || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Interceptions</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.intercept || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Blocks</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.block || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Saves</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.save || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Penalty Saves</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.penalty_save || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Cross Claims</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.cross_claim || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Goals Conceded</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.concede || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Discipline Section - Table Style */}
                      <div>
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Discipline</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Fouls</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.foul || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Yellow Cards</td>
                              <td className="py-2 text-right font-semibold text-yellow-600">{teamDetail.yellow_card || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Red Cards</td>
                              <td className="py-2 text-right font-semibold text-red-600">{teamDetail.red_card || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Detail tim tidak tersedia</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div 
          className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-4"
          onClick={() => setShowUnlockModal(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-ltb-r text-white p-4 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
              </div>
              <h3 className="font-bold text-lg">Unlock Statistik Pemain</h3>
            </div>

            {/* Modal Content */}
            <div className="p-5">
              <p className="text-gray-600 text-center mb-4">
                Untuk melihat detail statistik seluruh pemain, Anda perlu membayar sekali:
              </p>
              
              <div className="bg-blue-50 rounded-xl p-4 text-center mb-4">
                <p className="text-3xl font-bold text-ltb-bright">{unlockCost}</p>
                <p className="text-sm text-gray-500">Token</p>
              </div>
              
              <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3 mb-4">
                <span className="text-sm text-gray-600">Saldo Token Anda:</span>
                <span className={`font-bold ${tokensBalance >= unlockCost ? 'text-green-600' : 'text-red-600'}`}>
                  {tokensBalance} Token
                </span>
              </div>
              
              {tokensBalance < unlockCost && (
                <p className="text-red-500 text-sm text-center mb-4">
                  Token tidak cukup! Silakan top up terlebih dahulu.
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnlockModal(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                {tokensBalance >= unlockCost ? (
                  <button
                    onClick={handleUnlock}
                    disabled={unlocking}
                    className="flex-1 py-3 bg-gradient-ltb-r text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {unlocking ? 'Memproses...' : 'Unlock Sekarang'}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/topup')}
                    className="flex-1 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
                  >
                    Top Up Token
                  </button>
                )}
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                * Setelah unlock, Anda dapat melihat detail statistik semua pemain secara permanen
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default StatistikPage;
